/**
 * ReviewPaymentModal.jsx - Part 4 Implementation
 * 
 * Checklist Items Covered:
 * - J1: Open Balance Display
 * - J2: Amount Input
 * - J3: Confirm Screen
 * - J4: Platform Notice
 */

import React, { useState } from 'react';
import {
  X, AlertTriangle, CheckCircle, CreditCard, Clock, ArrowRight
} from 'lucide-react';
import {
  formatMoney,
  formatFullDate,
  getVendorContract,
  getVendorPaid,
  getVendorRemaining,
  getLastPayment,
  getPaymentCount,
} from '../../lib/vendorInsights';
import { theme } from '../../data/colors';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  },
  modal: {
    background: theme.bgCard,
    borderRadius: 14,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
    border: `1px solid ${theme.borderMedium}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.borderLight}`,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: theme.textPrimary,
  },
  subtitle: {
    fontSize: 12,
    color: theme.textMuted,
    marginTop: 2,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  content: {
    padding: 24,
  },

  // J1: Open Balance Display
  balanceCard: {
    padding: '20px',
    borderRadius: 14,
    background: theme.bgTableDivider,
    border: `1px solid ${theme.borderLight}`,
    textAlign: 'center',
    marginBottom: 20,
  },
  balanceLabel: {
    fontSize: 11,
    color: theme.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginBottom: 6,
  },
  balanceValue: {
    fontSize: 32,
    fontWeight: 700,
    color: '#C8783A',
    letterSpacing: '-0.5px',
  },
  balanceValuePaid: {
    color: theme.statusSuccess,
  },
  balanceMeta: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'center',
    gap: 20,
    fontSize: 12,
    color: theme.textMuted,
  },

  // Payment summary
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 20,
  },
  summaryCard: {
    padding: '14px',
    borderRadius: 12,
    background: theme.bgCard,
    border: `1px solid ${theme.borderLight}`,
  },
  summaryLabel: {
    fontSize: 10,
    color: theme.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.textPrimary,
  },
  summaryValueGreen: {
    color: theme.statusSuccess,
  },

  // Payment history preview
  historySection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: theme.textMuted,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: theme.bgCard,
    borderRadius: 10,
    border: `1px solid ${theme.borderLight}`,
    marginBottom: 6,
  },
  historyLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  historyIcon: {
    width: 28,
    height: 28,
    borderRadius: 7,
    background: theme.statusSuccessTint,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  historyAmount: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.statusSuccess,
  },
  historyDate: {
    fontSize: 11,
    color: theme.textMuted,
  },
  emptyHistory: {
    padding: '20px',
    textAlign: 'center',
    color: theme.textMuted,
    fontSize: 12,
    background: theme.bgCard,
    borderRadius: 10,
  },

  // J4: Platform Notice
  platformNotice: {
    padding: '14px 16px',
    borderRadius: 12,
    background: theme.statusWarningTint,
    border: `1px solid ${theme.borderMedium}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 20,
  },
  platformNoticeText: {
    fontSize: 12,
    color: '#92400e',
    lineHeight: '18px',
  },

  // J2: Quick Payment Amount
  quickAmountSection: {
    marginBottom: 20,
  },
  quickAmountGrid: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  quickAmountBtn: {
    flex: 1,
    padding: '12px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 12,
    background: theme.bgCard,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: theme.textSecondary,
    transition: 'all 0.15s',
  },
  quickAmountBtnActive: {
    borderColor: theme.accentPrimary,
    background: theme.statusWarningTint,
    color: '#876B2C',
  },
  customAmountWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  customAmountInput: {
    flex: 1,
    padding: '10px 12px',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: theme.textPrimary,
    background: theme.bgCard,
  },

  // J3: Confirm Section
  confirmSection: {
    padding: '16px',
    borderRadius: 14,
    background: theme.statusSuccessTint,
    border: `1px solid rgba(47,122,87,0.18)`,
    marginBottom: 20,
  },
  confirmTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.statusSuccess,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  },
  confirmLabel: {
    fontSize: 12,
    color: theme.statusSuccess,
  },
  confirmValue: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.statusSuccess,
  },

  // Actions
  actions: {
    display: 'flex',
    gap: 12,
  },
  cancelBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'transparent',
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  logPaymentBtn: {
    flex: 2,
    padding: '12px 24px',
    background: theme.accentPrimary,
    border: 'none',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    color: theme.textInverse,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
};

const ReviewPaymentModal = ({ vendor, onClose, onLogPayment }) => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const contract = getVendorContract(vendor);
  const paid = getVendorPaid(vendor);
  const remaining = getVendorRemaining(vendor);
  const payments = vendor?.payments || [];
  const lastPayment = getLastPayment(vendor);
  const paymentCount = getPaymentCount(vendor);
  const isFullyPaid = remaining === 0 && contract > 0;

  // Use the actual recorded deposit only — never fabricate a 50%-of-contract
  // figure. When no deposit has been recorded, we simply omit the Deposit
  // shortcut rather than inventing one.
  const deposit = Math.max(Number(vendor?.deposit_amount) || 0, 0);

  const quickAmounts = [
    ...(deposit > 0 ? [{ label: 'Deposit', value: deposit }] : []),
    { label: 'Balance', value: remaining },
    { label: 'Custom', value: null },
  ];

  const activeAmount = selectedAmount === 'custom' 
    ? (parseFloat(customAmount) || 0) 
    : (selectedAmount || 0);

  const handleProceed = () => {
    if (activeAmount > 0) {
      setShowConfirm(true);
    }
  };

  const handleLogPayment = () => {
    onLogPayment(vendor, activeAmount);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} data-testid="review-payment-modal">
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Review Payments</h2>
            <div style={styles.subtitle}>{vendor?.company_name}</div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} color={theme.textMuted} />
          </button>
        </div>

        <div style={styles.content}>
          {/* J1: Open Balance Display */}
          <div style={styles.balanceCard}>
            <div style={styles.balanceLabel}>
              {isFullyPaid ? 'Status' : 'Open Balance'}
            </div>
            <div style={{
              ...styles.balanceValue,
              ...(isFullyPaid ? styles.balanceValuePaid : {}),
            }}>
              {isFullyPaid ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <CheckCircle size={28} /> Fully Paid
                </span>
              ) : (
                formatMoney(remaining)
              )}
            </div>
            <div style={styles.balanceMeta}>
              <span>{paymentCount} payment{paymentCount !== 1 ? 's' : ''} logged</span>
              <span>•</span>
              <span>{formatMoney(paid)} of {formatMoney(contract)}</span>
            </div>
          </div>

          {/* Payment Summary */}
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Contract Total</div>
              <div style={styles.summaryValue}>{formatMoney(contract)}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Paid</div>
              <div style={{ ...styles.summaryValue, ...styles.summaryValueGreen }}>
                {formatMoney(paid)}
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div style={styles.historySection}>
            <div style={styles.sectionTitle}>Payment History</div>
            {payments.length === 0 ? (
              <div style={styles.emptyHistory}>
                No payments have been logged yet
              </div>
            ) : (
              payments.slice(0, 3).map((payment, i) => (
                <div key={i} style={styles.historyItem}>
                  <div style={styles.historyLeft}>
                    <div style={styles.historyIcon}>
                      <CreditCard size={14} color={theme.statusSuccess} />
                    </div>
                    <div>
                      <div style={styles.historyAmount}>{formatMoney(payment.amount)}</div>
                      <div style={styles.historyDate}>{formatFullDate(payment.date)}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#8B8478' }}>
                    {payment.method || 'Payment'}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* J4: Platform Notice */}
          {!isFullyPaid && (
            <div style={styles.platformNotice}>
              <AlertTriangle size={18} color={theme.statusWarning} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={styles.platformNoticeText}>
                Payments are logged for record-keeping only. Actual payment must be made directly to the vendor through your preferred method.
              </div>
            </div>
          )}

          {/* J2: Quick Payment Amount Selection */}
          {!isFullyPaid && !showConfirm && (
            <div style={styles.quickAmountSection}>
              <div style={styles.sectionTitle}>Quick Payment</div>
              <div style={styles.quickAmountGrid}>
                {quickAmounts.map((qa) => (
                  <button
                    key={qa.label}
                    type="button"
                    onClick={() => {
                      if (qa.value === null) {
                        setSelectedAmount('custom');
                      } else {
                        setSelectedAmount(qa.value);
                        setCustomAmount('');
                      }
                    }}
                    style={{
                      ...styles.quickAmountBtn,
                      ...((selectedAmount === qa.value || (qa.value === null && selectedAmount === 'custom')) 
                        ? styles.quickAmountBtnActive 
                        : {}),
                    }}
                  >
                    {qa.value !== null ? formatMoney(qa.value) : qa.label}
                  </button>
                ))}
              </div>
              
              {selectedAmount === 'custom' && (
                <div style={styles.customAmountWrap}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: theme.textMuted }}>$</span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Enter amount"
                    style={styles.customAmountInput}
                    step="0.01"
                    min="0"
                    data-testid="custom-amount-input"
                  />
                </div>
              )}
            </div>
          )}

          {/* J3: Confirm Screen */}
          {showConfirm && (
            <div style={styles.confirmSection}>
              <div style={styles.confirmTitle}>Confirm Payment Details</div>
              <div style={styles.confirmRow}>
                <span style={styles.confirmLabel}>Payment Amount</span>
                <span style={styles.confirmValue}>{formatMoney(activeAmount)}</span>
              </div>
              <div style={styles.confirmRow}>
                <span style={styles.confirmLabel}>New Balance</span>
                <span style={styles.confirmValue}>
                  {formatMoney(Math.max(remaining - activeAmount, 0))}
                </span>
              </div>
              {activeAmount >= remaining && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: theme.statusSuccess, fontSize: 12 }}>
                  <CheckCircle size={14} />
                  This will mark the vendor as fully paid
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={styles.actions}>
            <button
              type="button"
              onClick={() => showConfirm ? setShowConfirm(false) : onClose()}
              style={styles.cancelBtn}
            >
              {showConfirm ? 'Back' : 'Close'}
            </button>
            
            {!isFullyPaid && (
              showConfirm ? (
                <button
                  type="button"
                  onClick={handleLogPayment}
                  style={styles.logPaymentBtn}
                  data-testid="confirm-log-payment"
                >
                  <CreditCard size={16} />
                  Log {formatMoney(activeAmount)}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleProceed}
                  disabled={!activeAmount || activeAmount <= 0}
                  style={{
                    ...styles.logPaymentBtn,
                    opacity: (!activeAmount || activeAmount <= 0) ? 0.6 : 1,
                  }}
                  data-testid="proceed-to-confirm"
                >
                  Proceed <ArrowRight size={16} />
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPaymentModal;
